import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { apiErrorMessage } from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import PluginCard from "../../components/PluginCard";
import { resolveImageUrl } from "../../lib/format";
import { setSeo, setJsonLd } from "../../lib/seo";

export default function CategoryPage() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [cats, setCats] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        setData(null); setError("");
        api.get(`/categories/${slug}`)
            .then(({ data }) => {
                setData(data);
                const c = data.category || {};
                const banner = c.banner_url || c.image_url;
                setSeo({
                    title: `${c.name} — Plugins de audio`,
                    description: c.description || `Explora los plugins AVX Pro de la categoría ${c.name}: VST3, AU y AAX para Windows y macOS.`,
                    image: banner ? resolveImageUrl(banner) : undefined,
                });
                const origin = window.location.origin;
                setJsonLd([
                    {
                        "@context": "https://schema.org", "@type": "CollectionPage",
                        name: `${c.name} — Plugins AVX Pro`,
                        description: c.description || undefined,
                        url: `${origin}/plugins/categoria/${c.slug}`,
                        hasPart: (data.products || []).map((p) => ({
                            "@type": "Product", name: p.name, url: `${origin}/plugins/${p.slug}`,
                            image: p.image_url ? resolveImageUrl(p.image_url) : undefined,
                        })),
                    },
                    {
                        "@context": "https://schema.org", "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Plugins", item: `${origin}/plugins` },
                            { "@type": "ListItem", position: 2, name: c.name, item: `${origin}/plugins/categoria/${c.slug}` },
                        ],
                    },
                ]);
            })
            .catch((e) => setError(apiErrorMessage(e)));
        api.get("/categories").then(({ data }) => setCats((data || []).filter((c) => c.product_count > 0))).catch(() => {});
    }, [slug]);

    if (error) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <div className="flex-1 flex items-center justify-center text-center">
                    <div>
                        <p className="text-avx-danger mb-4">{error}</p>
                        <Link to="/plugins" className="btn-secondary px-5 py-2 rounded-sm text-sm font-mono uppercase tracking-[0.18em]">Ver catálogo</Link>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    const cat = data?.category;
    const products = data?.products || [];

    return (
        <div className="min-h-screen flex flex-col" data-testid="category-page">
            <PublicHeader />
            <section className="border-b border-avx-line relative overflow-hidden">
                {(cat?.banner_url || cat?.image_url) && (
                    <div className="absolute inset-0 opacity-15">
                        <img src={resolveImageUrl(cat.banner_url || cat.image_url)} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-b from-avx-bg/80 to-avx-bg" />
                    </div>
                )}
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <Link to="/plugins" className="label-mono hover:text-avx-blue inline-block mb-5">← Catálogo</Link>
                    <div className="label-mono mb-3">/ Categoría</div>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
                        {cat ? cat.name : "…"}
                    </h1>
                    {cat?.description && <p className="text-avx-muted max-w-2xl text-lg leading-relaxed">{cat.description}</p>}
                    {cat && <div className="label-mono mt-4">{products.length} plugin{products.length === 1 ? "" : "s"}</div>}
                </div>
            </section>

            {cats.length > 0 && (
                <div className="border-b border-avx-line">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap gap-2" data-testid="category-nav">
                        <Link to="/plugins" className="px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-[0.15em] border border-avx-line text-avx-muted hover:border-avx-blue/40">Todas</Link>
                        {cats.map((c) => (
                            <Link
                                key={c.id}
                                to={`/plugins/categoria/${c.slug}`}
                                data-testid={`category-nav-${c.slug}`}
                                className={`px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-[0.15em] border transition-colors ${c.slug === slug ? "border-avx-blue bg-avx-blue/15 text-avx-blue" : "border-avx-line text-avx-muted hover:border-avx-blue/40"}`}
                            >
                                {c.name} <span className="opacity-60">({c.product_count})</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 flex-1 w-full">
                {!data ? (
                    <div className="text-avx-muted font-mono">Cargando…</div>
                ) : products.length === 0 ? (
                    <div className="text-avx-muted font-mono">No hay plugins en esta categoría todavía.</div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map((p) => <PluginCard key={p.id} product={p} />)}
                    </div>
                )}
            </section>
            <PublicFooter />
        </div>
    );
}
